import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { formatMoney } from "@/lib/api";

// Preview-only mockup showing the proposed "Group Income By Source" UX
// vs the current flat list. Wired to a route (/app/preview/income-grouping)
// so the user can decide whether to promote the grouped variant to the
// real Step 3. Not linked from anywhere else — direct URL only.
export default function PreviewIncomeGrouping() {
  const sampleFlat = [
    { source: "Acme Corp", freq: "weekly", date: "Aug 7 (Fri)", amount: 800 },
    { source: "Acme Corp", freq: "weekly", date: "Aug 14 (Fri)", amount: 800 },
    { source: "Acme Corp", freq: "weekly", date: "Aug 21 (Fri)", amount: 800 },
    { source: "Acme Corp", freq: "weekly", date: "Aug 28 (Fri)", amount: 800 },
    { source: "Rental income", freq: "monthly", date: "Aug 1 (Sat)", amount: 1200 },
    { source: "Amazon side gig", freq: "one-time", date: "Aug 22 (Sat)", amount: 300 },
  ];
  const grouped = [
    {
      source: "Acme Corp", freq: "weekly", total: 3200, events: [
        { date: "Aug 7 (Fri)", amount: 800 },
        { date: "Aug 14 (Fri)", amount: 800 },
        { date: "Aug 21 (Fri)", amount: 800 },
        { date: "Aug 28 (Fri)", amount: 800 },
      ],
    },
    { source: "Rental income", freq: "monthly", total: 1200, events: [{ date: "Aug 1 (Sat)", amount: 1200 }] },
    { source: "Amazon side gig", freq: "one-time", total: 300, events: [{ date: "Aug 22 (Sat)", amount: 300 }] },
  ];
  const monthTotal = 4700;

  return (
    <div className="min-h-screen bg-[#EFE9DE] py-10 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-[#8A918D]">Preview mockup</div>
            <h1 className="font-heading text-3xl text-[#2C302E] mt-1">Group Income By Source</h1>
            <p className="text-sm text-[#59605C] mt-2 max-w-2xl">
              A visual comparison of the current flat list vs the proposed grouped-by-source list.
              Nothing is live yet — this page is a preview so you can decide before I ship it.
            </p>
          </div>
          <Link to="/app/dashboard" className="btn-ghost text-sm">← Back to Dashboard</Link>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* BEFORE */}
          <div className="card-soft p-6" data-testid="preview-flat">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-[#8A918D] mb-2">Before · Current flat list</div>
            <div className="flex items-center justify-between border-b border-[#EBE6E0] pb-2 mb-3">
              <div className="font-heading text-lg text-[#2F4E32]">Step 3 · Money Coming In</div>
              <div className="text-lg font-heading font-bold text-[#2F4E32]">+{formatMoney(monthTotal)}</div>
            </div>
            <div className="divide-y divide-[#EBE6E0]">
              {sampleFlat.map((r, i) => (
                <div key={i} className="py-2.5 flex items-center justify-between">
                  <div className="text-sm text-[#2C302E]">
                    <span className="font-medium">{r.source}</span>
                    <span className="text-[#8A918D]"> · {r.date}</span>
                    <span className="ml-2 text-[10px] uppercase tracking-widest text-[#8A918D]">· {r.freq}</span>
                  </div>
                  <div className="text-sm font-semibold text-[#2F4E32]">+{formatMoney(r.amount)}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 text-xs text-[#59605C] italic leading-relaxed">
              Every occurrence gets its own row. For a weekly earner this stacks up fast — 4 rows of &ldquo;Acme Corp&rdquo; + everything else on the same visual level. Hard to see the source totals at a glance.
            </div>
          </div>

          {/* AFTER */}
          <div className="card-soft p-6" data-testid="preview-grouped">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-[#4A6148] mb-2">Proposed · Grouped by source</div>
            <div className="flex items-center justify-between border-b border-[#EBE6E0] pb-2 mb-3">
              <div className="font-heading text-lg text-[#2F4E32]">Step 3 · Money Coming In</div>
              <div className="text-lg font-heading font-bold text-[#2F4E32]">+{formatMoney(monthTotal)}</div>
            </div>
            <div className="space-y-3">
              {grouped.map((g, i) => (
                <details key={i} open className="rounded-xl border border-[#D8CFB9] bg-white overflow-hidden">
                  <summary className="cursor-pointer list-none px-4 py-2.5 flex items-center justify-between hover:bg-[#F5F1EA]">
                    <div className="flex items-center gap-2 min-w-0">
                      <ChevronDown size={14} className="text-[#2F4E32] shrink-0" />
                      <span className="font-semibold text-[#2C302E] truncate">{g.source}</span>
                      <span className="text-[11px] uppercase tracking-widest text-[#8A918D] shrink-0">· {g.freq}</span>
                    </div>
                    <div className="text-sm font-bold text-[#2F4E32] whitespace-nowrap">
                      +{formatMoney(g.total)}
                    </div>
                  </summary>
                  <div className="px-4 pb-3 pt-1 divide-y divide-[#F0EADB]">
                    {g.events.map((ev, j) => (
                      <div key={j} className="py-1.5 flex items-center justify-between text-sm pl-4">
                        <span className="text-[#59605C]">{ev.date}</span>
                        <span className="text-[#2F4E32]">+{formatMoney(ev.amount)}</span>
                      </div>
                    ))}
                  </div>
                </details>
              ))}
            </div>
            <div className="mt-4 text-xs text-[#4A6148] italic leading-relaxed">
              Each income source becomes its own collapsible card with a bold total. Individual pay dates live inside — visible when open, tucked away when closed. Scans in seconds: &ldquo;I earn $3,200 from Acme, $1,200 rental, $300 side gig.&rdquo;
            </div>
          </div>
        </div>

        <div className="card-soft p-6 mt-8 max-w-3xl mx-auto">
          <div className="font-heading text-lg text-[#2C302E] mb-3">Design decisions if we ship this</div>
          <ul className="space-y-2 text-sm text-[#59605C]">
            <li>• <strong className="text-[#2C302E]">Groups auto-expand</strong> by default — you see everything without tapping. Users can collapse groups they want out of the way.</li>
            <li>• <strong className="text-[#2C302E]">One-time incomes</strong> (no recurrence) render as a single-row group (no need for a nested list).</li>
            <li>• <strong className="text-[#2C302E]">Frequency chip</strong> (weekly / biweekly / monthly / one-time) stays inline with the source name.</li>
            <li>• <strong className="text-[#2C302E]">Edit</strong> still opens the existing IncomeModal — no functional regression.</li>
            <li>• Same visual language as Step 1&apos;s grouped account rows so the whole forecast feels consistent.</li>
          </ul>
          <div className="mt-5 flex flex-wrap gap-2">
            <Link to="/app/dashboard" className="btn-primary text-sm">← Back to Dashboard</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
